import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { salesApi, inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * FR-SALES-02 — Quotations page.
 * List + detail + create modal + send/accept/reject/convert actions.
 */
export function QuotesPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const listQuery = useQuery({
    queryKey: ['sales', 'quotes', statusFilter],
    queryFn: () => salesApi.listQuotes({ status: statusFilter || undefined, limit: 200 }),
  });

  const expireMut = useMutation({
    mutationFn: () => salesApi.expireQuotes(),
    onSuccess: (d) => { toast.success(`${d.expired} quote${d.expired === 1 ? '' : 's'} marked expired`); qc.invalidateQueries({ queryKey: ['sales', 'quotes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const deleteQuoteMut = useMutation({
    mutationFn: (id: string) => salesApi.deleteQuote(id),
    onSuccess: (r: any) => { toast.success(r?.message || 'Draft quotation deleted'); qc.invalidateQueries({ queryKey: ['sales', 'quotes'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = (() => {
    const rows = listQuery.data ?? [];
    let draft = 0, sent = 0, accepted = 0, totalValue = 0;
    for (const q of rows) {
      if (q.status === 'draft') draft++;
      if (q.status === 'sent') sent++;
      if (q.status === 'accepted') accepted++;
      totalValue += parseFloat(q.grand_total) || 0;
    }
    return { total: rows.length, draft, sent, accepted, totalValue };
  })();

  const STATUS_OPTIONS = [
    { value: '',          label: 'All' },
    { value: 'draft',     label: 'Draft' },
    { value: 'sent',      label: 'Sent' },
    { value: 'accepted',  label: 'Accepted' },
    { value: 'rejected',  label: 'Rejected' },
    { value: 'expired',   label: 'Expired' },
    { value: 'converted', label: 'Converted' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Commerce"
        title="Quotations"
        subtitle="Quote lifecycle — create, send, accept, convert to a sales order."
        actions={
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={expireMut.isPending} onClick={() => expireMut.mutate()}>
              {expireMut.isPending ? 'Sweeping…' : 'Sweep expired'}
            </button>
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + New quote
            </button>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In view" value={kpis.total} />
        <KpiTile label="Awaiting response" value={kpis.sent}
          accent={kpis.sent > 0 ? 'amber' : 'default'} />
        <KpiTile label="Accepted" value={kpis.accepted} accent="sage" />
        <KpiTile label="Total value" value={fmtMoney(kpis.totalValue)} />
      </div>

      {/* Status filter chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_34rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? <LoadingState rows={5} /> : listQuery.error ? <ErrorState error={listQuery.error} /> :
           !listQuery.data?.length ? (
            <EmptyState
              message={statusFilter ? `No ${statusFilter} quotes` : 'No quotes yet'}
              hint={statusFilter ? 'Try a different filter.' : 'Create your first quote to begin tracking sales opportunities.'}
              action={!statusFilter && (
                <button className="btn-primary" onClick={() => setShowCreate(true)}>
                  + New quote
                </button>
              )}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Quote #</th>
                    <th>Customer</th>
                    <th>Valid until</th>
                    <th>Status</th>
                    <th className="text-right">Total</th>
                    <th className="text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {(listQuery.data ?? []).map((qt: any) => (
                    <tr key={qt.id} onClick={() => setSelected(qt.id)}
                      className={`cursor-pointer ${selected === qt.id ? '!bg-canvas-subtle' : ''}`}>
                      <td className="font-mono text-xs font-semibold text-ink-900">{qt.quote_number}</td>
                      <td className="text-sm text-ink-700 font-medium">{qt.customer_name}</td>
                      <td className="text-xs text-ink-500">
                        {qt.valid_until
                          ? new Date(qt.valid_until).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td><StatusBadge status={qt.status} /></td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                        ${parseFloat(qt.grand_total).toFixed(2)}
                      </td>
                      <td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                        {qt.status === 'draft' && (
                          <button
                            className="text-xs font-medium px-2 py-1 rounded transition-colors text-red-600 hover:bg-red-50"
                            disabled={deleteQuoteMut.isPending}
                            onClick={() => { if (window.confirm(`Delete draft quotation ${qt.quote_number}?`)) deleteQuoteMut.mutate(qt.id); }}>
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select a quote"
              hint="Click a row on the left to view items and lifecycle actions."
            />
          ) : (
            <QuoteDetail quoteId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['sales'] })} />
          )}
        </div>
      </div>

      {showCreate && <CreateQuoteModal onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          qc.invalidateQueries({ queryKey: ['sales', 'quotes'] });
          setShowCreate(false); setSelected(id);
        }} />}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = {
    draft: 'bg-canvas-subtle text-ink-700 ring-ink-200',
    sent: 'bg-blue-100 text-blue-700',
    accepted: 'bg-sage-50 text-sage-700 ring-sage-200',
    rejected: 'bg-red-50 text-red-700 ring-red-200',
    expired: 'bg-amber-100 text-amber-700',
    converted: 'bg-purple-100 text-purple-700',
  }[status] ?? 'bg-canvas-subtle';
  return <span className={`badge ${cls}`}>{status}</span>;
}

function QuoteDetail({ quoteId, onChanged }: { quoteId: string; onChanged: () => void }) {
  const qc = useQueryClient();
  const [showReject, setShowReject] = useState(false);
  const [showConvert, setShowConvert] = useState(false);

  const q = useQuery({ queryKey: ['sales', 'quote', quoteId], queryFn: () => salesApi.getQuote(quoteId) });

  const sendMut = useMutation({
    mutationFn: () => salesApi.sendQuote(quoteId),
    onSuccess: (d) => { toast.success(`Sent to ${d.recipient}`); qc.invalidateQueries({ queryKey: ['sales', 'quote', quoteId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const decideMut = useMutation({
    mutationFn: ({ accept, reason }: any) => salesApi.decideQuote(quoteId, accept, reason),
    onSuccess: (d) => {
      toast.success(`Marked ${d.status}`);
      qc.invalidateQueries({ queryKey: ['sales', 'quote', quoteId] });
      onChanged();
      setShowReject(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const convertMut = useMutation({
    mutationFn: () => salesApi.convertQuote(quoteId),
    onSuccess: (d) => {
      toast.success(`Converted to order ${d.orderNumber}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['sales'] });
      onChanged();
      setShowConvert(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const qt = q.data;

  const canSend = ['draft', 'sent'].includes(qt.status);
  const canDecide = ['draft', 'sent'].includes(qt.status);
  const canConvert = ['draft', 'sent', 'accepted'].includes(qt.status) && !qt.converted_order_id;

  return (
    <div>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-mono text-xs text-slate-500">{qt.quote_number}</div>
          <div className="font-semibold">{qt.customer_name}</div>
          {qt.customer_email && <div className="text-xs text-slate-600">{qt.customer_email}</div>}
          <div className="text-xs text-slate-500">
            Quote {new Date(qt.quote_date).toLocaleDateString()}
            {qt.valid_until && ` · valid until ${new Date(qt.valid_until).toLocaleDateString()}`}
          </div>
          {qt.sales_rep_name && <div className="text-xs text-slate-500">Rep: {qt.sales_rep_name}</div>}
        </div>
        <StatusBadge status={qt.status} />
      </div>

      {qt.sent_at && (
        <div className="text-xs text-slate-500 mb-3">
          Sent {new Date(qt.sent_at).toLocaleString()} to {qt.last_sent_to} by {qt.sent_by_name}
        </div>
      )}
      {qt.rejected_at && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded mb-3">
          Rejected {new Date(qt.rejected_at).toLocaleString()}: {qt.rejection_reason}
        </div>
      )}
      {qt.converted_order_number && (
        <div className="bg-purple-50 border border-purple-200 text-purple-800 text-xs p-2 rounded mb-3">
          Converted to order <span className="font-mono">{qt.converted_order_number}</span>
          {qt.converted_at && ` on ${new Date(qt.converted_at).toLocaleDateString()}`}
        </div>
      )}

      <div className="overflow-x-auto mb-4">
      <table className="table text-xs">
        <thead><tr><th>#</th><th>Product</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Disc</th><th className="text-right">Tax</th><th className="text-right">Total</th></tr></thead>
        <tbody>
          {qt.lines.map((l: any) => (
            <tr key={l.id}>
              <td>{l.line_no}</td>
              <td>
                <div>{l.description}</div>
                {l.sku && <div className="font-mono text-2xs text-ink-500">{l.sku}</div>}
              </td>
              <td className="text-right">{parseFloat(l.quantity).toFixed(0)}</td>
              <td className="text-right">{fmtCurrency(parseFloat(l.unit_price))}</td>
              <td className="text-right text-xs">{parseFloat(l.discount_percent) > 0 ? `${l.discount_percent}%` : ''}</td>
              <td className="text-right text-xs">{fmtCurrency(parseFloat(l.tax_amount))}</td>
              <td className="text-right font-semibold">{fmtCurrency(parseFloat(l.line_total))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan={6} className="text-right text-xs text-ink-500">Subtotal</td><td className="text-right">{fmtCurrency(parseFloat(qt.subtotal))}</td></tr>
          {parseFloat(qt.discount_total) > 0 && (
            <tr><td colSpan={6} className="text-right text-xs text-ink-500">Discount</td><td className="text-right text-danger">−${parseFloat(qt.discount_total).toFixed(2)}</td></tr>
          )}
          {parseFloat(qt.tax_total) > 0 && (
            <tr><td colSpan={6} className="text-right text-xs text-ink-500">Tax</td><td className="text-right">{fmtCurrency(parseFloat(qt.tax_total))}</td></tr>
          )}
          {parseFloat(qt.shipping_total) > 0 && (
            <tr><td colSpan={6} className="text-right text-xs text-ink-500">Shipping</td><td className="text-right">{fmtCurrency(parseFloat(qt.shipping_total))}</td></tr>
          )}
          <tr className="font-bold"><td colSpan={6} className="text-right">Total</td><td className="text-right text-lg">{fmtCurrency(parseFloat(qt.grand_total))}</td></tr>
        </tfoot>
      </table>
      </div>

      {qt.emails?.length > 0 && (
        <div className="mb-3 text-xs">
          <h3 className="font-semibold text-slate-600 mb-1">Send history</h3>
          {qt.emails.map((e: any, i: number) => (
            <div key={i} className="text-xs text-slate-600">
              {new Date(e.sent_at ?? Date.now()).toLocaleString()} → {e.recipient_email} ({e.status})
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-slate-200 pt-3 flex gap-2 justify-end flex-wrap">
        {canSend && (
          <button className="btn-secondary" disabled={sendMut.isPending} onClick={() => sendMut.mutate()}>
            {qt.sent_at ? 'Resend' : 'Send email'}
          </button>
        )}
        {canDecide && (
          <>
            <button className="btn-secondary" disabled={decideMut.isPending}
              onClick={() => setShowReject(true)}>
              Mark rejected
            </button>
            <button className="btn-secondary" disabled={decideMut.isPending}
              onClick={() => decideMut.mutate({ accept: true })}>
              Mark accepted
            </button>
          </>
        )}
        {canConvert && (
          <button className="btn-primary" disabled={convertMut.isPending}
            onClick={() => setShowConvert(true)}>
            {convertMut.isPending ? 'Converting…' : 'Convert to order'}
          </button>
        )}
      </div>

      {showReject && (
        <Modal
          onClose={() => setShowReject(false)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow text-danger mb-1">Sales · Quote</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Reject quote?</h2>
            <p className="text-sm text-ink-500 mt-1 leading-relaxed">
              Mark this quote as rejected by the customer. Reason is optional.
            </p>
          </div>
          <RejectQuoteForm
            loading={decideMut.isPending}
            onClose={() => setShowReject(false)}
            onConfirm={(r) => decideMut.mutate({ accept: false, reason: r || undefined })}
          />
        </Modal>
      )}

      {showConvert && (
        <Modal
          onClose={() => setShowConvert(false)}
          overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
          <div className="px-6 pt-6 pb-2">
            <div className="eyebrow mb-1">Sales · Quote</div>
            <h2 className="text-xl font-bold font-display text-ink-900">Convert to sales order?</h2>
            <p className="text-sm text-ink-500 mt-2 leading-relaxed">
              A draft sales order will be created from this quote.
              <span className="text-ink-700 font-medium"> Stock is not reserved</span> until the order is confirmed.
            </p>
          </div>
          <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
            <button className="btn-secondary" onClick={() => setShowConvert(false)}>Cancel</button>
            <button className="btn-primary" disabled={convertMut.isPending} onClick={() => convertMut.mutate()}>
              {convertMut.isPending ? 'Converting…' : 'Convert to order'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RejectQuoteForm({ loading, onClose, onConfirm }: { loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <textarea className="input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Customer chose competitor, price too high" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700" disabled={loading}
          onClick={() => onConfirm(reason)}>
          {loading ? 'Saving…' : 'Mark rejected'}
        </button>
      </div>
    </>
  );
}

function CreateQuoteModal({ onClose, onCreated }:
    { onClose: () => void; onCreated: (id: string) => void }) {
  const in30 = (() => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10); })();
  const [customerId, setCustomerId] = useState('');
  const [validUntil, setValidUntil] = useState(in30);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<Array<any>>([
    { productId: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, taxRate: 0 },
  ]);

  const custQuery = useQuery({ queryKey: ['customers'], queryFn: () => salesApi.listCustomers({ limit: 500 }) });
  const prodQuery = useQuery({
    queryKey: ['products-short'],
    queryFn: () => inventoryApi.listProducts({ pageSize: 500, status: 'active' }),
  });
  const products = prodQuery.data?.data ?? [];

  const mut = useMutation({
    mutationFn: () => salesApi.createQuote({
      customerId, validUntil,
      notes: notes || undefined,
      lines: lines.filter((l) => l.productId && l.quantity > 0).map((l) => ({
        productId: l.productId,
        description: l.description || undefined,
        quantity: l.quantity, unitPrice: l.unitPrice,
        discountPercent: l.discountPercent,
        taxRate: l.taxRate,
      })),
    }),
    onSuccess: (d) => { toast.success(`Quote ${d.quoteNumber} created`); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const handleProductChange = (idx: number, productId: string) => {
    const prod = products.find((p: any) => p.id === productId);
    setLines((prev) => prev.map((l, j) => j === idx ? {
      ...l,
      productId,
      description: prod?.name ?? l.description,
      unitPrice: prod?.base_price ? parseFloat(prod.base_price) : l.unitPrice,
    } : l));
  };

  const grandTotal = lines.reduce((s, l) => {
    const gross = l.quantity * l.unitPrice;
    const disc = gross * (l.discountPercent / 100);
    const net = gross - disc;
    return s + net + net * (l.taxRate / 100);
  }, 0);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Sales · Quotes</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New quote</h2>
      </div>

      <div className="px-6 py-4 grid grid-cols-2 gap-3 flex-shrink-0">
        <div>
          <label className="label">Customer</label>
          <select className="input" value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Select…</option>
            {custQuery.data?.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Valid until</label>
          <input type="date" className="input" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </div>
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="border border-ink-100 rounded-xl overflow-hidden mb-3">
          <table className="table">
            <thead><tr><th>Product</th><th className="text-right">Qty</th><th className="text-right">Unit</th><th className="text-right">Disc %</th><th className="text-right">Tax %</th><th className="w-8"></th></tr></thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <select className="input text-xs" value={l.productId} onChange={(e) => handleProductChange(i, e.target.value)}>
                      <option value="">Select product…</option>
                      {products.map((p: any) => <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>)}
                    </select>
                  </td>
                  <td className="text-right"><input type="number" min={0.01} step={1} className="input w-20 text-right tabular-nums" value={l.quantity}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td className="text-right"><input type="number" min={0} step={0.01} className="input w-24 text-right tabular-nums" value={l.unitPrice}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitPrice: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td className="text-right"><input type="number" min={0} max={100} step={0.1} className="input w-16 text-right tabular-nums" value={l.discountPercent}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, discountPercent: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td className="text-right"><input type="number" min={0} max={100} step={0.1} className="input w-16 text-right tabular-nums" value={l.taxRate}
                    onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, taxRate: parseFloat(e.target.value) || 0 } : x))} /></td>
                  <td>
                    <button
                      className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                      onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}>✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button className="text-sm font-medium text-ink-700 hover:text-ink-900 transition-colors mb-3"
          onClick={() => setLines((prev) => [...prev, { productId: '', description: '', quantity: 1, unitPrice: 0, discountPercent: 0, taxRate: 0 }])}>
          + Add line
        </button>
        <div className="mb-3">
          <label className="label">Notes <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="text-right mb-3 font-display font-bold text-xl text-ink-900 tabular-nums">
          Total: ${grandTotal.toFixed(2)}
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!customerId || grandTotal <= 0 || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create quote'}
        </button>
      </div>
    </Modal>
  );
}


function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
